import {Observable} from "rxjs";
import {BarTreeGenerator, ScheduledEvent, Scheduler} from "@measure-up/rhythm";
import {Settings} from "./settings";
import {BarEvent} from "./bar-events";
import {createBarEventSource} from "./bar-event-source";

/**
 * Clicker-side adapter over the kernel {@link Scheduler}: wires the bar tree
 * generator and bar event source from {@link Settings} (read live, so tempo
 * changes apply within the look-ahead window and structural changes take
 * effect when the next bar is generated), and pre-schedules the count-in.
 */
class ClickScheduler {

    private readonly settings: Settings;
    private readonly scheduler: Scheduler<BarEvent>;
    readonly onsets$: Observable<ScheduledEvent<BarEvent>>;

    constructor(settings: Settings, currentTime: () => number) {
        this.settings = settings;

        const generator = new BarTreeGenerator(settings);
        this.scheduler = new Scheduler(
            {
                bpm: () => settings.bpm,
                source: createBarEventSource(generator, settings),
            },
            currentTime,
        );
        this.onsets$ = this.scheduler.events$;
    }

    /**
     * Pre-schedule the count-in (beatsPerBar evenly spaced, always sounding,
     * with the first as the downbeat) and start the look-ahead loop.
     */
    start() {
        const countIn: BarEvent[] = [];
        for (let i = 0; i < this.settings.beatsPerBar; i++) {
            countIn.push({duration: 1, isRest: false, isDownbeat: i === 0});
        }
        this.scheduler.start(countIn);
    }

    /**
     * Stop scheduling; the next start begins cleanly at a downbeat.
     */
    stop() {
        this.scheduler.stop();
    }
}

export {ClickScheduler};
